import React, {
	Fragment,
	useState,
	useCallback,
	memo,
	type MouseEvent,
	type KeyboardEvent,
	type ElementRef,
	useImperativeHandle,
	type FocusEvent,
} from 'react';
import { styled, jsx } from '@compiled/react';
import noop from 'lodash/noop';
import Button from '@atlaskit/button';
import CheckCircleIcon from '@atlaskit/icon/core/success';
import CheckCircleIconLegacy from '@atlaskit/icon/glyph/check-circle';
import LinkIcon from '@atlaskit/icon/core/migration/link';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { fontFallback } from '@atlaskit/theme/typography';
import { token } from '@atlaskit/tokens';
import { openViewFromEmbed } from '@atlassian/jira-business-common/src/common/utils/embeds/index.tsx';
import { SELECTED_ISSUE_PARAM } from '@atlassian/jira-business-constants/src/index.tsx';
import { usePrefetchIssueViewDataActions } from '@atlassian/jira-business-issue-view-critical-data-loader/src/controllers/use-prefetch-issue-view-data/index.tsx';
import FieldCopyText from '@atlassian/jira-common-components-field-copy-text/src/view.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { CellNavigationProps } from '../../../common/types/cell-navigation.tsx';
import HierarchyIcon from '../../../common/ui/hierarchy-icon/index.tsx';
import { WrapperCommon } from '../../../common/ui/list-cell/styled.tsx';
import {
	useHasCellSelection,
	useCellActions,
} from '../../../controllers/cell-navigation/index.tsx';
import { useJWMListFeatures } from '../../../controllers/features-context/index.tsx';
import { useHierarchyConfig } from '../../../controllers/hierarchy/hierarchy-config/index.tsx';
import messages from './messages.tsx';
import { IssueKey } from './issue-key/issue-key.tsx';

type Props = {
	issueId: string;
	issueKey: string;
	parentIssues?: { id: string; key: string }[] | null;
	onOpenIssueModal: (arg: {
		id: string;
		key: string;
	}) => (event: MouseEvent | KeyboardEvent) => void;
} & CellNavigationProps;

export const KeyCell = memo<Props>(
	({
		issueId,
		issueKey,
		parentIssues,
		onOpenIssueModal,
		isSelected = false,
		wrapperRef,
		onFocus,
		onBlur,
		onKeyDown,
		isHovering: isHoveringNew = false,
		cellRef,
	}: Props) => {
		const { formatMessage } = useIntl();
		const [isCopied, setIsCopied] = useState(false);
		const [isHoveringOld, setIsHovering] = useState(false);
		const isHovering = fg('jira_list_keyboard_nav') ? isHoveringNew : isHoveringOld;
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { issueViewInteraction } = useJWMListFeatures();
		const { showFlatList } = useHierarchyConfig();

		const { prefetchIssue } = usePrefetchIssueViewDataActions();

		const [{ hasSelection }] = useHasCellSelection();

		const handleIssueKeyMouseEnter = useCallback(() => {
			prefetchIssue(issueKey);
		}, [issueKey, prefetchIssue]);

		const { clearSelectedCell } = fg('jwm_list_new_copy_paste_behaviour')
			? // eslint-disable-next-line react-hooks/rules-of-hooks
				useCellActions()
			: { clearSelectedCell: noop };

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Use handleMouseLeave in CellNavigation for generic behaviour or useImperativeHandle
		 *  to provide custom override behaviour for this cell
		 */
		const handleMouseEnter = useCallback(() => setIsHovering(true), [setIsHovering]);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Use handleMouseEnter in CellNavigation for generic behaviour or useImperativeHandle
		 *  to provide custom override behaviour for this cell
		 */
		const handleMouseLeave = useCallback(() => setIsHovering(false), [setIsHovering]);

		const handleCopyClick = useCallback(() => {
			fireUIAnalytics(createAnalyticsEvent({}), 'keyCell copyIssueLink', {});
		}, [createAnalyticsEvent]);

		const handleCopyReset = useCallback(() => setIsCopied(false), [setIsCopied]);
		const handleClickChange = useCallback((status: boolean) => setIsCopied(status), []);

		const onEmbedIssueClick = useCallback(
			(event: MouseEvent | KeyboardEvent) => {
				if (fg('jwm_list_new_copy_paste_behaviour')) {
					clearSelectedCell();
				}

				fireUIAnalytics(createAnalyticsEvent({}), 'issue clicked', 'listEmbedIssue');
				openViewFromEmbed({ key: SELECTED_ISSUE_PARAM, value: issueKey });
				event.preventDefault();
				event.stopPropagation();
			},
			[clearSelectedCell, createAnalyticsEvent, issueKey],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Use handleKeyDown in CellNavigation for generic behaviour or handleKeyDownNew in this
		 * cell for custom behaviour
		 */
		const handleKeyDown = useCallback(
			(e: KeyboardEvent<HTMLDivElement>) => {
				if (e.key === 'Enter') {
					return;
				}
				onKeyDown?.(e);
			},
			[onKeyDown],
		);

		const onIssueKeyClick = useCallback(
			({ id, key }: { id: string; key: string }) =>
				(event: MouseEvent | KeyboardEvent) => {
					if (fg('jwm_list_new_copy_paste_behaviour')) {
						event.stopPropagation();
						clearSelectedCell();
					}

					fireUIAnalytics(createAnalyticsEvent({}), 'keyCell linkClicked');
					onOpenIssueModal({ id, key })(event);
				},
			[clearSelectedCell, createAnalyticsEvent, onOpenIssueModal],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated wrapperRef is now managed in CellNavigation
		 */
		const onWrapperRef = useCallback(
			(ref: ElementRef<'div'> | null) => {
				if (wrapperRef) {
					// eslint-disable-next-line no-param-reassign
					wrapperRef.current = ref;
				}
			},
			[wrapperRef],
		);

		const handleKeyDownNew = useCallback(
			(e: KeyboardEvent<HTMLElement>) => {
				if (e.key === 'Enter') {
					issueViewInteraction === 'openNewTab'
						? onEmbedIssueClick(e)
						: onIssueKeyClick({ id: issueId, key: issueKey })(e);
				}
				return false;
			},
			[issueId, issueKey, issueViewInteraction, onEmbedIssueClick, onIssueKeyClick],
		);

		const handleCopyIconFocus = useCallback((e: FocusEvent<HTMLElement>) => {
			// do not allow focus to propagate or it will select the cell
			fg('jwm_list_new_copy_paste_behaviour') && e.stopPropagation();
		}, []);

		useImperativeHandle(cellRef, () => ({ onClick: () => true, onKeyDown: handleKeyDownNew }), [
			handleKeyDownNew,
		]);

		let keyContent = (
			<IssueKey
				href={`/browse/${issueKey}`}
				onClick={
					issueViewInteraction === 'openNewTab'
						? onEmbedIssueClick
						: onIssueKeyClick({ id: issueId, key: issueKey })
				}
				onMouseEnter={handleIssueKeyMouseEnter}
				data-testid="business-list.ui.list-view.key-cell.issue-key"
			>
				{issueKey}
			</IssueKey>
		);

		if (showFlatList && parentIssues != null) {
			keyContent = (
				<IssueKeyContent>
					<ParentIssueKeysWrapper>
						{parentIssues.map(({ id, key }, index) => (
							<Fragment key={id}>
								{index > 0 && <ParentIssueKeysDivider>/</ParentIssueKeysDivider>}
								<IssueKey
									href={`/browse/${key}`}
									onClick={
										issueViewInteraction === 'openNewTab'
											? onEmbedIssueClick
											: onIssueKeyClick({ id, key })
									}
									data-testid="business-list.ui.list-view.key-cell.parent-issue-key"
								>
									{key}
								</IssueKey>
							</Fragment>
						))}
					</ParentIssueKeysWrapper>
					<IssueKeyHierarchyWrapper>
						<HierarchyIcon label="" size="small" />
						<IssueKey
							href={`/browse/${issueKey}`}
							onClick={
								issueViewInteraction === 'openNewTab'
									? onEmbedIssueClick
									: onIssueKeyClick({ id: issueId, key: issueKey })
							}
							data-testid="business-list.ui.list-view.key-cell.issue-key"
						>
							{issueKey}
						</IssueKey>
					</IssueKeyHierarchyWrapper>
				</IssueKeyContent>
			);
		}

		/**
		 * when copy and paste is enabled,
		 * - show the copy icon when:
		 * 		- hovering
		 * 		- issue key has just been copied - so the reset logic built into the copy icon can be triggered after 3 seconds
		 * - hide the copy icon when:
		 * 		- there is a selection in the list, so the copy action doesn't conflict with other cell's copy action
		 */
		const shouldShowCopyIcon = fg('jwm_list_new_copy_paste_behaviour')
			? (!hasSelection && isHovering) || isCopied
			: isHovering;

		const keyCellContent = (
			<>
				{keyContent}

				{shouldShowCopyIcon && (
					<CopyIconWrapper onFocus={handleCopyIconFocus}>
						<FieldCopyText
							id={`copy-${issueKey}`}
							label={formatMessage(messages.copy)}
							tooltipDelay={0}
							tooltipLabel={formatMessage(messages.copy)}
							tooltipLabelPosition="top"
							shouldFitContainer
							value={
								typeof window !== 'undefined' ? `${window.location.origin}/browse/${issueKey}` : ''
							}
							hideFieldText
							remountOnChange
							onCopyClick={handleCopyClick}
							onClickChange={handleClickChange}
							onCopyReset={handleCopyReset}
						>
							<Button
								appearance="subtle-link"
								iconBefore={
									isCopied ? (
										<CheckCircleIcon
											label={formatMessage(messages.copied)}
											color={token('color.icon.success', colors.G500)}
											LEGACY_primaryColor={token('color.background.success.bold', colors.G500)}
											LEGACY_fallbackIcon={CheckCircleIconLegacy}
											LEGACY_size="small"
										/>
									) : (
										<LinkIcon LEGACY_size="small" label={formatMessage(messages.copy)} />
									)
								}
								spacing="none"
							/>
						</FieldCopyText>
					</CopyIconWrapper>
				)}
			</>
		);

		if (fg('jira_list_keyboard_nav')) {
			return <Box xcss={issueKeyCellStyles}>{keyCellContent}</Box>;
		}

		return (
			<IssueKeyCell
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				onBlur={onBlur}
				tabIndex={0}
				onFocus={onFocus}
				onKeyDown={handleKeyDown}
				ref={onWrapperRef}
				isFocused={isSelected}
			>
				{keyCellContent}
			</IssueKeyCell>
		);
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CopyIconWrapper = styled.div({
	opacity: 'var(--copy-icon-opacity)',
	transform: 'var(--copy-icon-transform)',
	paddingRight: token('space.050'),
	height: token('space.200'),
	transition: 'all 0.2s ease',
	position: 'absolute',
	right: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueKeyCell = styled(WrapperCommon)<{
	isFocused?: boolean;
}>({
	paddingLeft: token('space.100'),
	paddingRight: token('space.100'),
	fontWeight: token('font.weight.medium'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N90),
	cursor: 'pointer',
	position: 'relative',
	borderRadius: token('border.radius'),
	boxSizing: 'border-box',
	'--copy-icon-opacity': 0,
	'--copy-icon-transform': `translate(${token('space.negative.100')}, 0)`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':hover': {
		'--copy-icon-opacity': 1,
		'--copy-icon-transform': 'translate(0, 0)',
	},
});

const issueKeyCellStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	height: '100%',
	outline: 'none',
	paddingLeft: 'space.100',
	paddingRight: 'space.100',
	fontWeight: token('font.weight.medium'),
	cursor: 'pointer',
	color: 'color.text',
	position: 'relative',
	'--copy-icon-opacity': 0,
	'--copy-icon-transform': `translate(${token('space.negative.100')}, 0)`,
	':hover': {
		// @ts-expect-error - TS2353 - Object literal may only specify known properties, and ''--copy-icon-opacity'' does not exist in type 'SafeCSSObject'
		'--copy-icon-opacity': 1,
		'--copy-icon-transform': 'translate(0, 0)',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueKeyContent = styled.div({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IssueKeyHierarchyWrapper = styled.div({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ParentIssueKeysWrapper = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	font: token('font.body.small', fontFallback.body.small),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ParentIssueKeysDivider = styled.div({
	paddingTop: 0,
	paddingRight: token('space.025'),
	paddingBottom: 0,
	paddingLeft: token('space.025'),
});
